import React, { useState, useEffect } from 'react';
import {
  Bell, BellRing, Building2, User, AlertCircle, CheckCircle,
  Mail, Phone, Globe, Trash2, Eye, EyeOff, RefreshCw, ArrowLeft, Filter,
  ChevronDown, Clock, ExternalLink, Settings, X, MessageSquare, Flag, Star
} from 'lucide-react';
import { adminNotifications, appSettings } from '../lib/supabase';

// ==================== NOTIFICATION ICON BY TYPE ====================
const NotificationIcon = ({ type, className = "w-5 h-5" }) => {
  const icons = {
    business_submission: Building2,
    new_user: User,
    data_correction: AlertCircle,
    contact_submission: MessageSquare,
    provider_report: Flag,
    provider_review: Star
  };
  const Icon = icons[type] || Bell;
  return <Icon className={className} />;
};

// ==================== NOTIFICATION COLORS BY TYPE ====================
const getTypeConfig = (type) => {
  const configs = {
    business_submission: {
      bg: 'bg-purple-100',
      text: 'text-purple-700',
      border: 'border-purple-200',
      gradient: 'from-purple-500 to-purple-600',
      label: 'Business Listing'
    },
    new_user: {
      bg: 'bg-blue-100',
      text: 'text-blue-700',
      border: 'border-blue-200',
      gradient: 'from-blue-500 to-blue-600',
      label: 'New User'
    },
    data_correction: {
      bg: 'bg-red-100',
      text: 'text-red-700',
      border: 'border-red-200',
      gradient: 'from-red-500 to-red-600',
      label: 'Data Correction'
    },
    contact_submission: {
      bg: 'bg-brand-100',
      text: 'text-brand-700',
      border: 'border-brand-200',
      gradient: 'from-brand-500 to-brand-600',
      label: 'Contact Message'
    },
    provider_report: {
      bg: 'bg-orange-100',
      text: 'text-orange-700',
      border: 'border-orange-200',
      gradient: 'from-orange-500 to-orange-600',
      label: 'Provider Report'
    },
    provider_review: {
      bg: 'bg-amber-100',
      text: 'text-amber-700',
      border: 'border-amber-200',
      gradient: 'from-amber-500 to-amber-600',
      label: 'Provider Review'
    }
  };
  return configs[type] || { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200', gradient: 'from-gray-500 to-gray-600', label: type };
};

// ==================== FORMAT TIME AGO ====================
const timeAgo = (date) => {
  const now = new Date();
  const then = new Date(date);
  const seconds = Math.floor((now - then) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return then.toLocaleDateString('en-JM', { month: 'short', day: 'numeric' });
};

// ==================== NOTIFICATION CARD ====================
const NotificationCard = ({ notification, onMarkRead, onDelete, onView }) => {
  const config = getTypeConfig(notification.type);
  const data = notification.data || {};

  return (
    <div className={`bg-white rounded-xl shadow-lg overflow-hidden border ${notification.is_read ? 'border-gray-100 opacity-75' : `${config.border} border-l-4`}`}>
      <div className="p-4">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${config.bg}`}>
            <NotificationIcon type={notification.type} className={`w-6 h-6 ${config.text}`} />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div>
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text} mb-1`}>
                  {config.label}
                </span>
                <h4 className="font-bold text-gray-900">{notification.title}</h4>
                <p className="text-sm text-gray-600 mt-0.5">{notification.message}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {notification.email_sent && (
                  <span className="p-1 text-brand-500" title="Email sent">
                    <Mail className="w-4 h-4" />
                  </span>
                )}
                {!notification.is_read && (
                  <span className="w-2 h-2 bg-brand-500 rounded-full" />
                )}
              </div>
            </div>

            {/* Data Preview */}
            <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-1.5 text-sm">
              {notification.type === 'business_submission' && (
                <>
                  <p><span className="text-gray-500">Business:</span> <span className="font-medium">{data.business_name}</span></p>
                  <p><span className="text-gray-500">Type:</span> <span className="font-medium capitalize">{data.business_type}</span></p>
                  <p><span className="text-gray-500">Contact:</span> <span className="font-medium">{data.contact_name}</span></p>
                  {data.contact_email && (
                    <p className="flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5 text-gray-400" />
                      <a href={`mailto:${data.contact_email}`} className="text-brand-600 hover:underline">{data.contact_email}</a>
                    </p>
                  )}
                  {data.contact_phone && (
                    <p className="flex items-center gap-1">
                      <Phone className="w-3.5 h-3.5 text-gray-400" />
                      <a href={`tel:${data.contact_phone}`} className="text-brand-600 hover:underline">{data.contact_phone}</a>
                    </p>
                  )}
                </>
              )}

              {notification.type === 'new_user' && (
                <>
                  <p><span className="text-gray-500">Name:</span> <span className="font-medium">{data.full_name || 'Not provided'}</span></p>
                  <p><span className="text-gray-500">Email:</span> <span className="font-medium">{data.email}</span></p>
                  <p><span className="text-gray-500">Type:</span> <span className="font-medium capitalize">{data.user_type === 'service_provider' ? 'Education Provider' : 'Parent/Student'}</span></p>
                  {data.parish && <p><span className="text-gray-500">Parish:</span> <span className="font-medium">{data.parish}</span></p>}
                </>
              )}

              {notification.type === 'data_correction' && (
                <>
                  <p><span className="text-gray-500">Entity:</span> <span className="font-medium">{data.entity_type}</span></p>
                  <p><span className="text-gray-500">Name:</span> <span className="font-medium">{data.entity_name}</span></p>
                  <p><span className="text-gray-500">Correction:</span> <span className="font-medium">{data.correction_type}</span></p>
                  {data.description && <p><span className="text-gray-500">Details:</span> <span className="font-medium">{data.description}</span></p>}
                </>
              )}

              {notification.type === 'contact_submission' && (
                <>
                  <p><span className="text-gray-500">From:</span> <span className="font-medium">{data.name}</span></p>
                  <p><span className="text-gray-500">Subject:</span> <span className="font-medium">{data.subject}</span></p>
                  <p><span className="text-gray-500">Category:</span> <span className="font-medium capitalize">{data.category?.replace('_', ' ')}</span></p>
                  {data.email && (
                    <p className="flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5 text-gray-400" />
                      <a href={`mailto:${data.email}`} className="text-brand-600 hover:underline">{data.email}</a>
                    </p>
                  )}
                  {data.phone && (
                    <p className="flex items-center gap-1">
                      <Phone className="w-3.5 h-3.5 text-gray-400" />
                      <a href={`tel:${data.phone}`} className="text-brand-600 hover:underline">{data.phone}</a>
                    </p>
                  )}
                  {data.message && (
                    <p className="mt-2 p-2 bg-white rounded border border-gray-200 text-sm italic">
                      "{data.message.length > 150 ? data.message.substring(0, 150) + '...' : data.message}"
                    </p>
                  )}
                </>
              )}

              {notification.type === 'provider_report' && (
                <>
                  <p><span className="text-gray-500">Provider:</span> <span className="font-medium">{data.provider_name}</span></p>
                  <p><span className="text-gray-500">Reason:</span> <span className="font-medium capitalize">{data.reason?.replace('_', ' ')}</span></p>
                  {data.description && <p><span className="text-gray-500">Details:</span> <span className="font-medium">{data.description}</span></p>}
                  {data.reporter_email && (
                    <p className="flex items-center gap-1">
                      <Mail className="w-3.5 h-3.5 text-gray-400" />
                      <a href={`mailto:${data.reporter_email}`} className="text-brand-600 hover:underline">{data.reporter_email}</a>
                    </p>
                  )}
                </>
              )}

              {notification.type === 'provider_review' && (
                <>
                  <p><span className="text-gray-500">Provider:</span> <span className="font-medium">{data.provider_name}</span></p>
                  <p><span className="text-gray-500">Reviewer:</span> <span className="font-medium">{data.reviewer_name}</span></p>
                  <p><span className="text-gray-500">Rating:</span> <span className="font-medium">{'★'.repeat(data.rating || 0)}{'☆'.repeat(5 - (data.rating || 0))}</span></p>
                  {data.title && <p><span className="text-gray-500">Title:</span> <span className="font-medium">{data.title}</span></p>}
                  {data.body && (
                    <p className="mt-2 p-2 bg-white rounded border border-gray-200 text-sm italic">
                      "{data.body.length > 150 ? data.body.substring(0, 150) + '...' : data.body}"
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Actions & Time */}
            <div className="mt-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                {!notification.is_read && (
                  <button
                    onClick={() => onMarkRead(notification.id)}
                    className="flex items-center gap-1 px-3 py-1.5 bg-brand-100 text-brand-700 rounded-lg text-sm font-medium hover:bg-brand-200"
                  >
                    <CheckCircle className="w-4 h-4" />Mark Read
                  </button>
                )}
                <button
                  onClick={() => onDelete(notification.id)}
                  className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                >
                  <Trash2 className="w-4 h-4" />Delete
                </button>
              </div>
              <span className="text-xs text-gray-400 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {timeAgo(notification.created_at)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== NOTIFICATION BELL (FOR HEADER) ====================
export const NotificationBell = ({ onClick }) => {
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    loadUnreadCount();

    // Subscribe to new notifications
    const subscription = adminNotifications.subscribe((newNotification) => {
      setUnreadCount(prev => prev + 1);
    });

    return () => subscription?.unsubscribe();
  }, []);

  const loadUnreadCount = async () => {
    const { count } = await adminNotifications.getUnreadCount();
    setUnreadCount(count);
  };

  return (
    <button
      onClick={onClick}
      className="relative p-2 bg-white/10 rounded-lg hover:bg-white/20 transition-colors"
    >
      {unreadCount > 0 ? (
        <BellRing className="w-5 h-5 text-white animate-pulse" />
      ) : (
        <Bell className="w-5 h-5 text-white" />
      )}
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  );
};

// ==================== NOTIFICATIONS PAGE ====================
export const AdminNotificationsPage = ({ setPage }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, type filters
  const [typeFilter, setTypeFilter] = useState('all');
  const [showSettings, setShowSettings] = useState(false);
  const [adminEmails, setAdminEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [emailEnabled, setEmailEnabled] = useState(true);

  useEffect(() => {
    loadNotifications();
    loadSettings();

    // Subscribe to real-time notifications
    const subscription = adminNotifications.subscribe((newNotification) => {
      setNotifications(prev => [newNotification, ...prev]);
    });

    return () => subscription?.unsubscribe();
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [filter, typeFilter]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filter === 'unread') filters.is_read = false;
      if (typeFilter !== 'all') filters.type = typeFilter;

      const { data } = await adminNotifications.getAll(filters);
      setNotifications(data || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const { data: emails } = await appSettings.get('admin_notification_emails');
      const { data: enabled } = await appSettings.get('notification_email_enabled');
      setAdminEmails(emails || []);
      setEmailEnabled(enabled !== 'false');
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const handleMarkRead = async (id) => {
    await adminNotifications.markAsRead(id);
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, is_read: true } : n)
    );
  };

  const handleMarkAllRead = async () => {
    await adminNotifications.markAllAsRead();
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this notification?')) return;
    await adminNotifications.delete(id);
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleAddEmail = async () => {
    if (!newEmail || !newEmail.includes('@')) return;
    const updatedEmails = [...adminEmails, newEmail];
    await appSettings.set('admin_notification_emails', updatedEmails);
    setAdminEmails(updatedEmails);
    setNewEmail('');
  };

  const handleRemoveEmail = async (email) => {
    const updatedEmails = adminEmails.filter(e => e !== email);
    await appSettings.set('admin_notification_emails', updatedEmails);
    setAdminEmails(updatedEmails);
  };

  const handleToggleEmail = async () => {
    const newValue = !emailEnabled;
    await appSettings.set('notification_email_enabled', newValue.toString());
    setEmailEnabled(newValue);
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 to-brand-600 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
              <Bell className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-black">Admin Notifications</h2>
              <p className="text-brand-100">
                {unreadCount > 0 ? `${unreadCount} unread notification${unreadCount !== 1 ? 's' : ''}` : 'All caught up!'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSettings(true)}
              className="p-2 bg-white/10 rounded-lg hover:bg-white/20"
              title="Email Settings"
            >
              <Settings className="w-5 h-5" />
            </button>
            <button onClick={loadNotifications} className="p-2 bg-white/10 rounded-lg hover:bg-white/20">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-lg flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-brand-500 focus:outline-none"
          >
            <option value="all">All Notifications</option>
            <option value="unread">Unread Only</option>
          </select>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-brand-500 focus:outline-none"
          >
            <option value="all">All Types</option>
            <option value="business_submission">Business Submissions</option>
            <option value="new_user">New Users</option>
            <option value="data_correction">Data Corrections</option>
            <option value="contact_submission">Contact Messages</option>
            <option value="provider_report">Provider Reports</option>
            <option value="provider_review">Provider Reviews</option>
          </select>
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="px-4 py-2 bg-brand-100 text-brand-700 rounded-lg font-medium hover:bg-brand-200 flex items-center gap-2"
          >
            <CheckCircle className="w-4 h-4" />Mark All Read
          </button>
        )}
      </div>

      {/* Notifications List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 text-brand-600 animate-spin" />
        </div>
      ) : notifications.length === 0 ? (
        <div className="bg-white rounded-xl p-12 text-center shadow-lg">
          <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-700">No Notifications</h3>
          <p className="text-gray-500 mt-2">
            {filter === 'unread' ? 'All notifications have been read.' : 'No notifications to display.'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {notifications.map(notification => (
            <NotificationCard
              key={notification.id}
              notification={notification}
              onMarkRead={handleMarkRead}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}

      {/* Email Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="p-6 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Mail className="w-6 h-6 text-brand-600" />
                Email Notification Settings
              </h3>
              <button onClick={() => setShowSettings(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-bold">Email Notifications</p>
                  <p className="text-sm text-gray-500">Send emails for new notifications</p>
                </div>
                <button
                  onClick={handleToggleEmail}
                  className={`w-12 h-6 rounded-full transition-colors ${emailEnabled ? 'bg-brand-500' : 'bg-gray-300'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform ${emailEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {/* Admin Emails */}
              <div>
                <p className="font-bold mb-3">Admin Email Addresses</p>
                <div className="space-y-2 mb-4">
                  {adminEmails.length === 0 ? (
                    <p className="text-sm text-gray-500 italic">No email addresses configured. Admins will be looked up from profiles.</p>
                  ) : (
                    adminEmails.map(email => (
                      <div key={email} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm">{email}</span>
                        <button
                          onClick={() => handleRemoveEmail(email)}
                          className="p-1 text-red-500 hover:bg-red-100 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="admin@example.com"
                    className="flex-1 px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-brand-500 focus:outline-none"
                    onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
                  />
                  <button
                    onClick={handleAddEmail}
                    className="px-4 py-2 bg-brand-500 text-white rounded-lg font-medium hover:bg-brand-600"
                  >
                    Add
                  </button>
                </div>
              </div>

              <div className="p-4 bg-amber-50 rounded-xl text-sm text-amber-800">
                <p className="font-medium">Note:</p>
                <p className="mt-1">If no emails are configured here, notifications will be sent to all users with admin or super_admin roles.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Admin Dashboard
      </button>
    </div>
  );
};

export default AdminNotificationsPage;
